<script lang="ts" setup>
import type { ProductListItem, ProductPriceInfo } from "~~/models/products";
import type { CatalogPageRecord, LayoutRecord } from "~~/server/graphql/datocms/generated";
import type { CreativeEventData } from "~/composables/useGtmTracking";

const { product, prices, index, trackCreativeEventsContext } = defineProps<{
  layout: LayoutRecord;
  cmsData: CatalogPageRecord;
  product?: ProductListItem | null | undefined;
  prices: Array<ProductPriceInfo> | undefined;
  isPricesLoading?: boolean;
  overpackPopper: { placement: "top" | "bottom" };
  trackCreativeEventsContext?: string;
  index?: number;
}>();

const { t } = useI18n();
const localePath = useLocalePath();
const { loggedIn } = useUserSession();

const { activeOverpack, activePrice, update: updateOverpack } = useOverpack(() => product, () => prices);

const { trackCreativeDisplayEvent, trackCreativeClickEvent } = useGtmTracking();
const trackCreatives = product && index !== undefined && trackCreativeEventsContext;
const creativeName = `${trackCreativeEventsContext} - Card ${(index || 0) + 1}`;
const creativeData: CreativeEventData = {
  creativeTitle: product?.name,
  creativeFileTitle: product?.mainImage?.name,
  creativeFileName: product?.mainImage?.relativeUrl || "<missing image>",
  creativeProductCode: product?.number,
  creativeBrandName: product?.dataLayer.brand,
};

onMounted(() => {
  if (!trackCreatives) {
    return;
  }

  trackCreativeDisplayEvent(
    creativeName,
    creativeData,
  );
});

function trackClick() {
  if (!trackCreatives) {
    return;
  }

  trackCreativeClickEvent(
    creativeName,
    creativeData,
  );
}
</script>

<template>
  <div v-if="product" class="flex flex-col bg-white hover:bg-[#F3F1EC]">
    <NuxtLinkLocale :to="product.detailUrl" @click="trackClick">
      <ProductImageWrapper
        :is-frozen-product="product.isFrozenProduct"
        :is-limited="product.isLimited"
        :is-new="product.isNew"
        :is-sale="product.isSale"
        :is-variable-weight="product.isVariableWeight"
        :show="{ iconLabel: false, zoom: false }"
        class="h-[220px] rounded-t-lg"
      >
        <ProductImage
          :image-url="product.mainImage?.relativeUrl"
          :image-alt="product.name"
          class="h-[215px] object-cover mix-blend-multiply"
        />
      </ProductImageWrapper>
    </NuxtLinkLocale>

    <hr class="border-2 border-[#F8F7F4]">

    <div class="gap-xs p-sm flex min-w-0 flex-col">
      <div>
        <span v-if="loggedIn" class="line-clamp-1 text-[13px] text-neutral-400">{{ product.number }}</span>

        <p class="line-clamp-2 h-[52px] wrap-break-word text-[16px] font-semibold leading-6" :title="product.name">
          <NuxtLinkLocale :to="product.detailUrl" @click="trackClick">
            {{ product.name }}
          </NuxtLinkLocale>
        </p>

        <span v-if="!loggedIn" class="block h-[24px] text-xs font-medium text-neutral-400">
          {{ product.defaultOverpack?.singleName }}</span>
      </div>

      <div v-if="!loggedIn" class="flex justify-end">
        <UButton variant="solid" color="primary" size="md" :to="localePath(`/${layout.registrationButtonLink.slug}`)">
          {{ t("product.action.registration") }}
        </UButton>
      </div>

      <div v-if="loggedIn" class="gap-2xs flex flex-col">
        <ProductPrice
          view-mode="list"
          :price="activePrice" :is-prices-loading="isPricesLoading"
          :is-variable-weight="product.isVariableWeight ?? false"
        />

        <div class="flex h-[26px]">
          <ProductOrderBadge v-if="product.categoryInfo.isBackorderProduct" />
        </div>
      </div>
    </div>

    <hr class="border-2 border-[#F8F7F4]">

    <div v-if="loggedIn" class="p-sm rounded-b-lg">
      <ProductAddToBasket
        :cms-data="cmsData"
        :product="product"
        :overpack="activeOverpack"
        :overpack-popper="overpackPopper"
        :add-to-basket="{ show: { button: true, label: false }, class: '', containerClass: 'justify-end', listName: 'Results' }"
        :add-to-template="{ show: { button: false } }"
        :replacements="{ show: false }"
        @updated-overpack="updateOverpack"
      />
    </div>
  </div>
</template>

<style scoped>

</style>
